<template>
  <div>
    <div v-if="editor && !isDisabled">
      <div class="d-flex toolbar">
        <div
          variant="none"
          class="toolbar-button"
          @click="editor.can().undo() ? editor.chain().focus().undo().run() : undefined"
          :disabled="!editor.can().undo()"
          :class="!editor.can().undo() ? 'disabled' : ''"
          :style="{ opacity: !editor.can().undo() ? 0.5 : 1 }"
        >
          <i class="fas fa-undo fa-lg"></i>
        </div>
        <div
          class="toolbar-button"
          @click="editor.can().redo() ? editor.chain().focus().redo().run() : undefined"
          :disabled="!editor.can().redo()"
          :class="!editor.can().redo() ? 'disabled' : ''"
          :style="{ opacity: !editor.can().redo() ? 0.5 : 1 }"
        >
          <i class="fas fa-redo fa-lg"></i>
        </div>
        <span class="vertical-seperator"></span>

        <div
          @click="editor.chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor.isActive('bold') }"
          class="toolbar-button"
        >
          <i class="fas fa-bold fa-lg"></i>
        </div>
        <div
          @click="editor.chain().focus().toggleItalic().run()"
          class="toolbar-button"
          :class="{ 'is-active': editor.isActive('italic') }"
        >
          <i class="fas fa-italic fa-lg"></i>
        </div>
        <div
          @click="editor.chain().focus().toggleUnderline().run()"
          class="toolbar-button"
          :class="{ 'is-active': editor.isActive('underline') }"
        >
          <i class="fas fa-underline fa-lg"></i>
        </div>
        <span class="vertical-seperator"></span>
        <div class="toolbar-button d-flex justify-items-center align-items-center">
          <b-button class="p-0 w-100 h-100" :id="'color-palette-' + uniqueId" variant="none" ref="button">
            <i class="fas fa-palette fa-lg p-0" :style="{ color: colorSelected }"></i>
          </b-button>

          <b-popover
            :target="'color-palette-' + uniqueId"
            placement="bottom"
            triggers="focus"
            custom-class="color-palette"
          >
            <div v-for="color in colors" :key="'palette-color-' + color" class="palette-color">
              <div
                style="width: 24px; height: 24px; border-radius: 3px; cursor: pointer"
                :style="{
                  border: colorSelected === color ? '1px solid #000' : '1px solid #bfc4c9',
                  opacity: colorSelected === color ? '1px solid #000' : '1px solid #bfc4c9',
                  backgroundColor: color,
                }"
                @click="clickPaletteColor(color)"
              ></div>
            </div>
          </b-popover>
        </div>
        <div class="toolbar-button" @click="editor.chain().focus().setColor(null).run()">
          <i class="fas fa-tint-slash fa-lg"></i>
        </div>
        <span class="vertical-seperator"></span>
        <div
          class="toolbar-button"
          @click="editor.chain().focus().toggleBulletList().run()"
          :class="{ 'is-active': editor.isActive('bulletList') }"
        >
          <i class="fas fa-list-ul fa-lg"></i>
        </div>
        <div
          class="toolbar-button"
          @click="editor.chain().focus().toggleOrderedList().run()"
          :class="{ 'is-active': editor.isActive('orderedList') }"
        >
          <i class="fas fa-list-ol fa-lg"></i>
        </div>
        <span class="vertical-seperator"></span>
        <div class="toolbar-button d-flex justify-items-center align-items-center">
          <b-button class="p-0 w-100 h-100" :id="'table-' + uniqueId" variant="none">
            <i class="fas fa-table fa-lg p-0"></i>
          </b-button>

          <b-popover :target="'table-' + uniqueId" placement="bottom" triggers="focus">
            <div class="d-flex" style="flex-direction: column; gap: 4px">
              <div class="d-flex" style="gap: 10px">
                <b-form-group label="Zeilen">
                  <b-input v-model="tableRows"></b-input>
                </b-form-group>
                <b-form-group label="Spalten">
                  <b-input v-model="tableCols"></b-input>
                </b-form-group>
              </div>
              <b-button
                size="sm"
                @click="
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows: tableRows, cols: tableCols, withHeaderRow: false })
                    .run()
                "
              >
                Erstelle Tabelle
              </b-button>
            </div>
          </b-popover>
        </div>
        <div style="display: flex; gap: 4px">
          <div
            class="table-edit-buttons"
            @click="editor.chain().focus().addRowAfter().run()"
            v-if="isCursorInTable"
          >
            <b style="font-size: 0.8rem">+ Zeile</b>
          </div>
          <div
            class="table-edit-buttons"
            @click="editor.chain().focus().deleteRow().run()"
            v-if="isCursorInTable"
          >
            <b style="font-size: 0.8rem">- Zeile</b>
          </div>
          <div
            class="table-edit-buttons"
            @click="editor.chain().focus().addColumnAfter().run()"
            v-if="isCursorInTable"
          >
            <b style="font-size: 0.8rem">+ Spalte</b>
          </div>
          <div
            class="table-edit-buttons"
            @click="editor.chain().focus().deleteColumn().run()"
            v-if="isCursorInTable"
          >
            <b style="font-size: 0.8rem">- Spalte</b>
          </div>
          <div
            class="table-edit-buttons"
            @click="editor.chain().focus().deleteTable().run()"
            v-if="isCursorInTable"
          >
            <b style="font-size: 0.8rem">- Tabelle</b>
          </div>
        </div>
      </div>
    </div>
    <editor-content :editor="editor" />
    <!-- Lightbox-Overlay zur Anzeige eines angeklickten Bildes in voller Größe -->
    <div v-if="lightboxSrc" class="editor-image-lightbox" @click="lightboxSrc = null">
      <img :src="lightboxSrc" alt="" />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import createSuggestion from './suggestion.js';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import FileHandler from '@tiptap/extension-file-handler';

export default {
  components: {
    EditorContent,
  },
  props: {
    value: {
      type: String,
    },
    placeholder: { type: String },
    isDisabled: { type: Boolean, default: false },
    disableMentions: { type: Boolean, default: false },
    mentionGroups: { type: Array, default: () => [{ name: 'Buchhaltung', id: 'Buchhaltung' }] },
  },
  computed: {
    colorSelected() {
      return this.editor?.getAttributes('textStyle')?.color;
    },
    isCursorInTable() {
      return this.editor?.isActive('table');
    },
  },
  data() {
    return {
      editor: null,
      popup: null,
      component: null,
      lightboxSrc: null,
      uniqueId: Math.random().toString(36).substring(2, 9),
      colors: [
        'rgb(0, 0, 0)',
        'rgb(77, 77, 77)',
        'rgb(153, 153, 153)',
        'rgb(230, 230, 230)',
        'rgb(255, 255, 255)',
        'rgb(230, 77, 77)',
        'rgb(230, 153, 77)',
        'rgb(230, 230, 77)',
        'rgb(153, 230, 77)',
        'rgb(77, 230, 77)',
        'rgb(77, 230, 153)',
        'rgb(77, 230, 230)',
        'rgb(77, 153, 230)',
        'rgb(77, 77, 230)',
        'rgb(153, 77, 230)',
      ],
      tableRows: 3,
      tableCols: 3,
    };
  },
  watch: {
    value(newValue) {
      if (this.editor && this.editor.getHTML() !== newValue) {
        this.editor.commands.setContent(newValue);
      }
    },
    isDisabled(newValue) {
      this.editor.setOptions({ editable: !newValue });
    },
  },
  mounted() {
    this.editor = new Editor({
      content: this.value,
      editable: !this.isDisabled,
      // Beim Klick auf ein Bild im Editor wird dieses in der Lightbox in voller Größe angezeigt
      editorProps: {
        handleClickOn: (view, pos, node) => {
          if (node.type.name === 'image') {
            this.lightboxSrc = node.attrs.src;
            return true;
          }
          return false;
        },
      },
      extensions: [
        StarterKit,
        // allowBase64 ist nötig, damit Bilder als als data-URL benutzt werden Bilder
        Image.configure({ allowBase64: true }),
        // FileHandler verarbeitet Bilder sowohl beim Einfügen als auch per Drag & Drop
        FileHandler.configure({
          allowedMimeTypes: ['image/png', 'image/jpeg', 'image/jpg'],
          onPaste: (currentEditor, files) => {
            this.handleImageFiles(currentEditor, files);
          },
          onDrop: (currentEditor, files, pos) => {
            this.handleImageFiles(currentEditor, files, pos);
          },
        }),
        Underline,
        Color,
        TextStyle,
        Link.configure({}),
        Placeholder.configure({
          placeholder: this.placeholder,
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        this.disableMentions
          ? null
          : Mention.configure({
              renderHTML({ node }) {
                return [
                  'span',
                  {
                    class: 'mention',
                    'data-mention-id': node.attrs.id,
                  },
                  `@${node.attrs.label}`,
                ];
              },
              suggestion: createSuggestion(this.mentionGroups),
            }),
      ],
      onUpdate: ({ editor }) => {
        const content = editor.getHTML();
        if (content !== this.value) {
          this.$emit('input', content);
        }
      },
    });
  },
  beforeDestroy() {
    // Destroy the editor and clean up any remaining Tippy instances
    if (this.editor) {
      this.editor.destroy();
    }
    if (this.popup) {
      this.popup[0].destroy();
    }
    if (this.component) {
      this.component.$destroy();
    }
  },
  methods: {
    // jedes Bild wird als Datei-Anhang weitergegeben und zusätzlich als Base64-data-URL inline in den Text eingefügt
    handleImageFiles(editor, files, pos) {
      Array.from(files)
        .filter(file => file.type.startsWith('image/'))
        .forEach(file => {
          const renamedFile = this.renameImageFile(file);
          // Das Event löst im Eltern-Component den Upload des Bildes als Datei-Anhang aus
          this.$emit('imagePaste', renamedFile);
          // Der FileReader wandelt die Bild-Datei in eine Base64-data-URL um
          const reader = new FileReader();
          reader.onload = () => {
            // Der Dateiname wird als alt-Attribut am Bild hinterlegt, damit das Inline-Bild
            // dieselbe Kennung wie der Datei-Anhang trägt und beim Entfernen abgeglichen werden kann
            const attrs = { src: reader.result, alt: renamedFile.name };
            if (pos != null) {
              editor.chain().focus().insertContentAt(pos, { type: 'image', attrs }).run();
            } else {
              editor.chain().focus().setImage(attrs).run();
            }
          };
          reader.readAsDataURL(renamedFile);
        });
    },
    // Erzeugt aus einem eingefügten Bild eine neue Datei mit sprechendem Namen im Format
    // "Bild_dd-mm-yyyy_HH-MM-ss-SSS.ext"; die Millisekunden sorgen für einen eindeutigen Namen,
    // damit das Inline-Bild später eindeutig dem Datei-Anhang zugeordnet werden kann
    renameImageFile(file) {
      const extension = file.type.split('/')[1] || 'png';
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      const date = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
      const time = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
      const ms = String(now.getMilliseconds()).padStart(3, '0');
      return new File([file], `Bild_${date}_${time}-${ms}.${extension}`, { type: file.type });
    },
    clickPaletteColor(color) {
      const colorChange = this.colorSelected === color ? null : color;
      this.editor.chain().focus().setColor(colorChange).run();
      this.$root.$emit('bv::hide::popover', 'color-palette');
    },
  },
};
</script>

<style lang="scss">
.table-edit-buttons {
  background-color: rgba(61, 37, 20, 0.12);
  border-radius: var(--radius-sm);
  padding: 2px;
  cursor: pointer;
}
.table-edit-buttons:hover {
  background-color: rgba(61, 37, 20, 0.32);
}

// Vollbild-Overlay
.editor-image-lightbox {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.8);
  cursor: zoom-out;

  img {
    max-width: 95%;
    max-height: 95%;
    object-fit: contain;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  }
}

/* Basic editor styles */
.tiptap {
  :first-child {
    margin-top: 0;
  }
  img {
    max-width: 100%;
    max-height: 400px;
    width: auto;
    height: auto;
    cursor: zoom-in;
  }

  table {
    border-collapse: collapse;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;

    td,
    th {
      border: 1px solid rgba(61, 37, 20, 0.12);
      box-sizing: border-box;
      min-width: 1em;
      padding: 6px 8px;
      position: relative;
      vertical-align: top;
      word-break: break-word;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      background-color: rgba(61, 37, 20, 0.05);
      font-weight: bold;
      text-align: left;
    }

    .selectedCell:after {
      background: rgba(61, 37, 20, 0.08);
      content: '';
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      pointer-events: none;
      position: absolute;
      z-index: 2;
    }

    .column-resize-handle {
      background-color: #800080;
      bottom: -2px;
      pointer-events: none;
      position: absolute;
      right: -2px;
      top: 0;
      width: 4px;
    }
  }

  .tableWrapper {
    overflow: auto;
    margin: 1.5rem 0;
    overflow-x: auto;
    width: 100%;
  }

  &.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }
  .mention {
    background-color: #dab1da;
    border-radius: 0.4rem;
    box-decoration-break: clone;
    color: #800080;
    padding: 0.1rem 0.3rem;
  }

  font-size: 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 12px;
  line-height: 1.3;

  a {
    color: #3e76f9;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #2563eb;
      text-decoration: none;
    }
  }
}
p.is-editor-empty:first-child::before {
  color: var(--text-muted);
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

.toolbar-button {
  border: none;
  width: 32px;
  height: var(--control-h-sm);
  border-radius: var(--radius-sm);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.toolbar-button.is-active {
  color: rgb(62, 118, 249);
  background-color: rgb(229, 240, 254);
}

.toolbar-button:not(.is-active):not(.disabled):hover {
  background-color: var(--border);
}
.toolbar-button.is-active:hover {
  background-color: rgb(210, 228, 251);
}

.toolbar {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background-color: #e5eaee20;
  padding: 3px;
  gap: 2px;
  display: flex;
  align-items: center;
}

.vertical-seperator {
  background-color: #bfc4c9;
  height: 16px;
  width: 1px;
  margin: 5px;
}

:deep(.dropdown) {
  width: 32px !important;
  height: 32px;
}

:deep(.b-popover) > :deep(.arrow) {
  visibility: hidden;
}

.color-palette > .popover-body {
  width: 160px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  padding: 12px;
  gap: 4px;
}

.palette-color:hover {
  opacity: 50%;
}

.beschreibung-expanded {
  overflow-y: auto;
  .tiptap {
    table {
      width: 100%;

      th,
      td {
        ul {
          padding-left: 20px;
          overflow-x: hidden;
        }
        ol {
          padding-left: 15px;
          overflow-x: hidden;
        }
      }
    }
  }
}
</style>
